import type { CSSProperties, HTMLAttributes } from "react";
import { cx } from "./cx";

export interface ProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** 0–100. Omit (or pass `indeterminate`) for the scanning state. */
  value?: number;
  indeterminate?: boolean;
  label?: string;
}

/** Determinate/indeterminate progress — bulk-geocode progress (M3) uses this. */
export function Progress({ value, indeterminate = false, label, className, ...rest }: ProgressProps) {
  const scanning = indeterminate || value == null;
  const clamped = scanning ? undefined : Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className={cx("pz-progress", scanning && "pz-progress--indeterminate", className)}
      {...rest}
    >
      <div className="pz-progress__fill" style={scanning ? undefined : { width: `${clamped}%` }} />
    </div>
  );
}

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: "text" | "block" | "circle";
  style?: CSSProperties;
}

/** Loading placeholder — opacity pulse, never a gradient shimmer (gradients are banned as identity). */
export function Skeleton({ variant = "text", className, ...rest }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={cx("pz-skeleton", variant === "text" && "pz-skeleton--text", variant === "circle" && "pz-skeleton--circle", className)}
      {...rest}
    />
  );
}
